import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { competitionsApi, type AssignParticipantRequest } from '@/api/competitions';
import type { PaymentMethod } from '@/api/types';

export const competitionParticipantKeys = {
  all: ['competition-participants'] as const,
  list: (competitionId: string) => [...competitionParticipantKeys.all, competitionId] as const,
};

/** Competitors currently assigned to a competition - GET /competitions/{competitionId}/participants. */
export function useParticipantsForCompetition(competitionId: string | undefined) {
  return useQuery({
    queryKey: competitionParticipantKeys.list(competitionId ?? ''),
    queryFn: () => competitionsApi.listParticipants(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useAssignParticipant(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignParticipantRequest) => competitionsApi.assignParticipant(competitionId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionParticipantKeys.list(competitionId) }),
  });
}

export function useRemoveParticipant(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => competitionsApi.removeParticipant(competitionId, participantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionParticipantKeys.list(competitionId) }),
  });
}

export function useSetParticipantPaid(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, hasPaid, method }: { participantId: string; hasPaid: boolean; method?: PaymentMethod }) =>
      competitionsApi.setParticipantPaid(competitionId, participantId, hasPaid, method),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionParticipantKeys.list(competitionId) }),
  });
}

export function useApproveParticipant(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => competitionsApi.approveParticipant(competitionId, participantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionParticipantKeys.list(competitionId) }),
  });
}

export function useRejectParticipant(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => competitionsApi.rejectParticipant(competitionId, participantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionParticipantKeys.list(competitionId) }),
  });
}
